import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useRef, useMemo, useCallback } from 'react';
import { mergeProps, useTranslation, useTokens, Box } from '@gemini/core';
import { useExitAnimation, useEnterAnimation } from '@gemini/ui/unsafe';
import { chain, useObjectRef } from '@react-aria/utils';
import { useToggleState } from '@react-stately/toggle';
import { useButton } from '@gemini/ui';

/**
 * Custom hook to map `MapPin` props to `Box` ones with all needed interactive and accessible HTML attributes.
 * @param ref MapPin element ref
 * @param props MapPin props
 * @returns Box props
 */ const useMapPin = (ref, props)=>{
    const { defaultSelected, isSelected, isHovered, onChange, onHoverChange, ...buttonProps } = props;
    const { isSelected: isToggleSelected, toggle } = useToggleState({
        ...defaultSelected !== undefined ? {
            defaultSelected
        } : {},
        ...isSelected !== undefined ? {
            isSelected
        } : {},
        onChange: onChange || (()=>{})
    });
    const { boxProps, ...rest } = useButton(ref, {
        ...buttonProps,
        onPress: chain(toggle, props.onPress),
        onHoverChange
    });
    return {
        ...rest,
        boxProps: mergeProps(boxProps, {
            'aria-pressed': isToggleSelected
        }),
        isSelected: isToggleSelected,
        isHovered: isHovered !== undefined ? isHovered : rest.isHovered
    };
};

const useMapPinTranslation = ()=>useTranslation({
        'en-GB': {
            'mappin::highLightLabel': 'New'
        },
        'fr-FR': {
            'mappin::highLightLabel': 'Nouveau'
        },
        'de-DE': {
            'mappin::highLightLabel': 'Neu'
        },
        'nl-NL': {
            'mappin::highLightLabel': 'Nieuw'
        }
    });

const arrowSquareDiagonal = 20;
const arrowHeight = arrowSquareDiagonal / 2;
const borderWidth = 'borderWidth.2';
const MapPin = /*#__PURE__*/ forwardRef(function MapPinButton(props, ref) {
    const { children, containerRef, variant = 'precise', containerSizeVariant = variant, onPress, onPressStart, onPressEnd, onFocusChange, onHoverChange, icon, iconOnly, isHighlighted, isSeen, 'aria-label': ariaLabel, defaultSelected, isSelected: isSelectedManually, isHovered: isHoveredManually, onChange, highlightTagProps, ...containerProps } = props;
    const { boxProps, isHovered, isPressed, isFocused, isSelected } = useMapPin(ref, props);
    const pointerRef = useRef(null);
    const iconRef = useRef(null);
    const isPointerExiting = useExitAnimation(pointerRef, variant === 'precise');
    const t = useMapPinTranslation();
    const tokens = useTokens();
    const isSmall = variant === 'small';
    // Always return same variant or bigger in size variants
    const containerSizeVariantComputed = useMemo(()=>{
        if (variant === 'small') return containerSizeVariant;
        if (variant === 'approx' && containerSizeVariant === 'small') return 'approx';
        if (variant === 'precise') return 'precise';
        return containerSizeVariant;
    }, [
        containerSizeVariant,
        variant
    ]);
    const stateProps = useCallback((variant)=>mapPinStateToBoxProps({
            isHovered,
            isPressed,
            isSelected,
            variant,
            isSeen
        }), [
        isHovered,
        isPressed,
        isSeen,
        isSelected
    ]);
    const { label: highLightLabel, ...tagRestProps } = highlightTagProps || {};
    const { as, ref: boxPropRef, type, tabIndex, 'aria-pressed': ariaPressed, ...restProps } = boxProps;
    const computePadding = useCallback((padding, variant)=>{
        return variant !== 'small' ? `calc(${tokens[iconOnly ? 'spacing.8' : padding]} - ${tokens[borderWidth]})` : undefined;
    }, [
        iconOnly,
        tokens
    ]);
    const computeSize = useCallback((variant)=>{
        if (variant === 'small') return {
            height: 16,
            width: 20
        };
        let height = 32;
        if (isHovered || isPressed || isSelected) height = 36;
        if (iconOnly) return {
            height,
            width: height
        };
        return {
            height,
            width: '100%'
        };
    }, [
        isSelected,
        iconOnly,
        isHovered,
        isPressed
    ]);
    const marginBottom = useCallback((variant)=>{
        let margin = 0;
        if (variant === 'precise') {
            margin += arrowHeight;
            if (iconOnly) {
                // Icon only variant is fully rounded, we have to cover more pixels to hide bottom border for background continuity
                margin -= 4;
            } else margin -= 1;
            if (isFocused) margin += 3;
        }
        return margin;
    }, [
        iconOnly,
        isFocused
    ]);
    const buttonContent = useCallback((variant)=>/*#__PURE__*/ jsxs(Box, {
            display: "inline-flex",
            gap: "spacing.4",
            alignItems: "center",
            transitionDuration: "motionDuration.150",
            transitionProperty: "transform",
            transitionTimingFunction: "motionEasing.ease",
            transform: variant === 'small' ? 'scale(0)' : 'scale(1)',
            children: [
                icon ? /*#__PURE__*/ jsx(IconElement, {
                    ref: iconRef,
                    icon: icon
                }) : null,
                !iconOnly && /*#__PURE__*/ jsx(Box, {
                    as: "span",
                    flexDirection: "column",
                    whiteSpace: "nowrap",
                    overflow: [
                        'hidden',
                        'clip'
                    ],
                    width: "100%",
                    textOverflow: "ellipsis",
                    children: children
                })
            ]
        }), [
        icon,
        iconOnly,
        children
    ]);
    return /*#__PURE__*/ jsxs(Box, {
        ref: containerRef,
        position: "relative",
        height: computeSize(containerSizeVariantComputed).height + (containerSizeVariantComputed === 'precise' ? marginBottom(containerSizeVariantComputed) : 0),
        width: "fit-content",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        alignItems: "center",
        ...containerProps,
        children: [
            variant === 'precise' && isHovered || isPointerExiting ? /*#__PURE__*/ jsx(Box, {
                ...restProps,
                as: "span",
                position: "absolute",
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                cursor: "pointer"
            }) : null,
            !isSmall && isHighlighted && !isSeen && !iconOnly && /*#__PURE__*/ jsx(Box, {
                display: "flex",
                position: "absolute",
                right: 0,
                bottom: computeSize(variant).height + marginBottom(variant),
                transform: "translateY(50%)",
                transitionDuration: "motionDuration.150",
                transitionProperty: "all",
                transitionTimingFunction: "motionEasing.ease",
                zIndex: "zIndex.2",
                children: /*#__PURE__*/ jsx(Box, {
                    ...tagRestProps,
                    ...restProps,
                    as: "span",
                    display: "inline-flex",
                    alignItems: "center",
                    whiteSpace: "nowrap",
                    backgroundColor: "color.surface.default.inverted.default",
                    color: "color.content.constant.white.default",
                    borderRadius: "radius.rounded",
                    paddingHorizontal: "spacing.6",
                    paddingVertical: "spacing.none",
                    typography: "typography.body.11.regular",
                    cursor: "pointer",
                    children: highLightLabel || t('mappin::highLightLabel')
                })
            }),
            /*#__PURE__*/ jsx(Box, {
                display: "flex",
                // Height is on the container already
                height: 0,
                width: computeSize(containerSizeVariantComputed).width,
                paddingHorizontal: computePadding('sizing.16', containerSizeVariantComputed),
                typography: stateProps(containerSizeVariantComputed).typography,
                borderLeftWidth: borderWidth,
                borderRightWidth: borderWidth,
                borderLeftStyle: "solid",
                borderRightStyle: "solid",
                borderLeftColor: "color.border.transparent.strong.default",
                borderRightColor: "color.border.transparent.strong.default",
                "aria-hidden": "true",
                visibility: "hidden",
                overflow: [
                    'hidden',
                    'clip'
                ],
                textOverflow: "ellipsis",
                userSelect: "none",
                pointerEvents: "none",
                // Keep animation here even if hidden as the width will still evolve with hover/focus
                transitionDuration: "motionDuration.150",
                transitionProperty: "all",
                transitionTimingFunction: "motionEasing.ease",
                children: buttonContent(containerSizeVariantComputed)
            }),
            /*#__PURE__*/ jsx(Box, {
                ...boxProps,
                ...stateProps(variant),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderWidth: borderWidth,
                borderStyle: "solid",
                borderRadius: "radius.rounded",
                borderColor: isSmall && !isSeen ? 'unsafe_component.mapPin.color.border' : 'color.border.transparent.strong.default',
                transitionDuration: "motionDuration.150",
                transitionProperty: "all",
                transitionTimingFunction: "motionEasing.ease",
                height: computeSize(variant).height,
                width: computeSize(variant).width,
                "aria-label": ariaLabel ? children : undefined,
                overflow: [
                    'hidden',
                    'clip'
                ],
                paddingHorizontal: computePadding('sizing.16', variant),
                paddingVertical: computePadding('sizing.6', variant),
                outlineWidth: borderWidth,
                outlineStyle: isFocused ? 'auto' : 'none',
                outlineColor: [
                    'Highlight',
                    '-webkit-focus-ring-color'
                ],
                outlineOffset: borderWidth,
                gap: "spacing.4",
                boxShadow: !(isHovered || isPressed || isSelected) && isSeen ? 'shadow.4' : 'shadow.8',
                cursor: "pointer",
                // Take into account the outlineOffset value when isFocused and precise variant because of animation to top
                marginBottom: variant === 'precise' && !isFocused ? Math.round(marginBottom(variant) + 1) : marginBottom(variant),
                children: buttonContent(variant)
            }),
            variant === 'precise' || isPointerExiting ? /*#__PURE__*/ jsx(MapPinPointer, {
                ...stateProps(variant),
                ref: pointerRef,
                variant: variant,
                isFocused: isFocused,
                boxProps: !isSmall ? restProps : {}
            }) : null
        ]
    });
});
const IconElement = /*#__PURE__*/ forwardRef(({ icon: Icon }, ref)=>{
    const objectRef = useObjectRef(ref);
    if (!Icon) return null;
    // Icon doesn't allow ref so we use a Box to wrap it
    return /*#__PURE__*/ jsx(Box, {
        as: "span",
        display: "inline-flex",
        ref: objectRef,
        width: "fit-content",
        height: "fit-content",
        children: /*#__PURE__*/ jsx(Icon, {
            size: "sizing.16",
            zIndex: "zIndex.1"
        })
    });
});
const mapPinStateToBoxProps = ({ isHovered, isPressed, isSelected, isSeen, variant })=>{
    const getBackgroundColor = ()=>{
        if ((isPressed || isHovered || isSelected) && variant !== 'small') return 'color.surface.default.inverted.default';
        if (isSeen) return 'color.surface.subdued.default';
        return 'color.surface.brand.primary.default';
    };
    const getColor = ()=>{
        if ((isPressed || isHovered || isSelected) && variant !== 'small') return 'color.content.default.inverted.default';
        if (isSeen) return 'color.content.default.default';
        return 'color.content.onPrimary.default';
    };
    const getTypography = ()=>{
        if (!isSeen && !(isPressed || isHovered || isSelected) && variant !== 'small') return 'typography.body.14.bold';
        if (isSeen && !(isPressed || isHovered || isSelected) && variant !== 'small') return 'typography.body.14.regular';
        return 'typography.body.16.bold';
    };
    return {
        backgroundColor: getBackgroundColor(),
        color: getColor(),
        typography: getTypography()
    };
};
const MapPinPointer = /*#__PURE__*/ forwardRef(({ variant, backgroundColor, isFocused, boxProps }, ref)=>{
    const objectRef = useObjectRef(ref);
    const isEntering = useEnterAnimation(objectRef);
    return /*#__PURE__*/ jsx(Box, {
        ref: objectRef,
        ...boxProps,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        // + 4 when focused and + 1 by default to avoid outline to be cut by the overflow clip
        height: isFocused ? arrowHeight + 4 : arrowHeight + 2,
        transitionDuration: "motionDuration.150",
        transitionProperty: "opacity, height",
        transitionTimingFunction: "motionEasing.ease",
        width: arrowSquareDiagonal,
        position: "absolute",
        left: "50%",
        transform: "translateX(-50%)",
        bottom: 0,
        opacity: variant !== 'precise' || isEntering ? 'opacity.none' : 'opacity.full',
        // Hide top part of the square that might overlap text/icon
        overflow: [
            'hidden',
            'clip'
        ],
        cursor: variant !== 'small' ? 'pointer' : undefined,
        children: /*#__PURE__*/ jsx(Box, {
            // Bigger than needed to have a half diagonal close to an integer value (to round it)
            height: 20,
            width: 20,
            borderRightWidth: borderWidth,
            borderBottomWidth: borderWidth,
            borderRightColor: "color.border.transparent.strong.default",
            borderBottomColor: "color.border.transparent.strong.default",
            position: "absolute",
            // Move it from half the diagonal to show half of the square rotated, with outlineOffset value
            top: Math.round(-(20 * Math.sqrt(2) / 2) + 1),
            transform: "rotate(45deg)",
            backgroundColor: backgroundColor,
            borderRightStyle: "solid",
            borderBottomStyle: "solid",
            outlineWidth: borderWidth,
            outlineStyle: isFocused ? 'auto' : 'none',
            outlineColor: [
                'Highlight',
                '-webkit-focus-ring-color'
            ],
            outlineOffset: "borderWidth.1",
            transitionDuration: "motionDuration.150",
            transitionProperty: "background-color",
            transitionTimingFunction: "motionEasing.ease"
        })
    });
});

export { MapPin };
//# sourceMappingURL=index.mjs.map
